import React, { useState, useEffect } from 'react';
import { TipSummary } from '../../layout/FundedTips/TipSummary';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import IconButton from '@material-ui/core/IconButton';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import SearchIcon from '@material-ui/icons/Search';
import PersonIcon from '@material-ui/icons/Person';
import ReceiptIcon from '@material-ui/icons/Receipt';
import { Skeleton } from '@material-ui/lab';

import { useStyles } from './LookupPage.styles';
import { LeftMenuDrawer } from '../../navigation/LeftMenuDrawer/LeftMenuDrawer';
import { SearchMenuBar } from '../../navigation/SearchMenuBar';
import useIsDesktop from '../../../hooks/useIsDesktop/useIsDesktop';
import {
  useCustomerSearch,
  useOrderReceipt,
  useOrderSearch,
} from '../../../hooks/useCustomerLookup/useCustomerLookup';
import { CustomerDetailPanel } from './CustomerDetailPanel';
import { OrderReceiptPanel } from './OrderReceiptPanel';
import { formatOrderEvent } from '../../../utils/formatOrderEvent';

function SkeletonRows({ count }) {
  const classes = useStyles();
  return (
    <>
      {Array.from({ length: count }).map((_, i) => (
        <Box key={i} className={classes.skeletonWrapper}>
          <Skeleton variant="text" width="60%" height={20} />
          <Skeleton variant="text" width="80%" height={16} style={{ marginTop: 4 }} />
        </Box>
      ))}
    </>
  );
}

SkeletonRows.propTypes = {
  count: PropTypes.number,
};

SkeletonRows.defaultProps = {
  count: 4,
};

function ErrorState({ message, onRetry }) {
  const classes = useStyles();
  return (
    <Box className={classes.errorContainer}>
      <Typography variant="body2" color="error">
        {message || 'Something went wrong.'}
      </Typography>
      {onRetry && (
        <Button size="small" onClick={onRetry} variant="outlined">
          Retry
        </Button>
      )}
    </Box>
  );
}

ErrorState.propTypes = {
  message: PropTypes.string,
  onRetry: PropTypes.func,
};

function CustomerResultsList({
  data,
  isLoading,
  error,
  page,
  onPageChange,
  onSelect,
  selectedCustomerId,
}) {
  const classes = useStyles();

  if (isLoading) {
    return <SkeletonRows count={5} />;
  }

  if (error) {
    return (
      <ErrorState
        message="Failed to load customers."
        onRetry={() => onPageChange(page)}
      />
    );
  }

  if (!data) {
    return null;
  }

  const results = data.results || [];

  if (results.length === 0) {
    return (
      <Box className={classes.emptyStateContainer}>
        <PersonIcon style={{ fontSize: 40, marginBottom: 8, opacity: 0.3 }} />
        <Typography variant="body2">No customers found.</Typography>
      </Box>
    );
  }

  const hasMore = results.length >= 20;
  const hasPrev = page > 1;

  return (
    <>
      <Box className={classes.resultsList}>
        {results.map((customer) => {
          const isSelected = String(customer.id) === String(selectedCustomerId);
          const fullName = [customer.firstName, customer.lastName]
            .filter(Boolean)
            .join(' ') || '—';
          const primaryPhone = customer.phone || '—';
          const primaryAddress = customer.primaryAddress || '—';
          return (
            <Box
              key={customer.id}
              className={clsx(classes.resultItem, {
                [classes.resultItemSelected]: isSelected,
              })}
              onClick={() => onSelect(customer.id)}
              role="button"
              tabIndex={0}
              onKeyPress={(e) => {
                if (e.key === 'Enter') onSelect(customer.id);
              }}
            >
              <Typography className={classes.resultItemPrimary}>
                {fullName}
              </Typography>
              <Typography className={classes.resultItemSecondary}>
                {customer.email || '—'}
              </Typography>
              <Typography className={classes.resultItemSecondary}>
                {primaryPhone} &middot; {primaryAddress}
              </Typography>
            </Box>
          );
        })}
      </Box>
      {(hasPrev || hasMore) && (
        <Box className={classes.paginationBar}>
          <Button
            size="small"
            disabled={!hasPrev}
            onClick={() => onPageChange(page - 1)}
          >
            Prev
          </Button>
          <Typography variant="caption" color="textSecondary">
            Page {page}
          </Typography>
          <Button
            size="small"
            disabled={!hasMore}
            onClick={() => onPageChange(page + 1)}
          >
            Next
          </Button>
        </Box>
      )}
    </>
  );
}

CustomerResultsList.propTypes = {
  data: PropTypes.object,
  isLoading: PropTypes.bool,
  error: PropTypes.any,
  page: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
  selectedCustomerId: PropTypes.any,
};

function CustomerTab({ onSelectCustomer, selectedCustomerId }) {
  const classes = useStyles();

  const [phone, setPhone] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [address, setAddress] = useState('');
  const [page, setPage] = useState(1);
  const [submittedParams, setSubmittedParams] = useState({});

  const { data, isLoading, error } = useCustomerSearch(submittedParams);

  const hasAnyInput = phone || firstName || lastName || address;

  const normalizePhone = (raw) => {
    const digits = raw.replace(/\D/g, '');
    if (digits.length === 10) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    if (digits.length === 7) {
      return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    }
    return digits;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const params = {};
    if (phone.trim()) params.phone = normalizePhone(phone);
    if (firstName.trim()) params.firstName = firstName.trim();
    if (lastName.trim()) params.lastName = lastName.trim();
    if (address.trim()) params.address = address.trim();
    params.page = 1;
    setPage(1);
    setSubmittedParams(params);
  };

  const handlePageChange = (newPage) => {
    const params = { ...submittedParams, page: newPage };
    setPage(newPage);
    setSubmittedParams(params);
  };

  const hasSubmitted = Object.keys(submittedParams).length > 0;

  return (
    <Box className={classes.leftPanelInner}>
      <Box
        component="form"
        className={classes.formContainer}
        onSubmit={handleSubmit}
        autoComplete="off"
      >
        <TextField
          label="Phone number"
          variant="outlined"
          size="small"
          fullWidth
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="e.g. 555-123-4567"
          style={{ marginBottom: 12 }}
          inputProps={{ 'aria-label': 'phone number' }}
        />
        <Box className={classes.fieldRow}>
          <TextField
            label="First name"
            variant="outlined"
            size="small"
            fullWidth
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            inputProps={{ 'aria-label': 'first name' }}
          />
          <TextField
            label="Last name"
            variant="outlined"
            size="small"
            fullWidth
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            inputProps={{ 'aria-label': 'last name' }}
          />
        </Box>
        <TextField
          label="Address"
          variant="outlined"
          size="small"
          fullWidth
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          style={{ marginBottom: 12 }}
          inputProps={{ 'aria-label': 'address' }}
        />
        <Button
          type="submit"
          variant="contained"
          color="primary"
          className={classes.submitButton}
          disabled={!hasAnyInput || isLoading}
          startIcon={
            isLoading ? <CircularProgress size={14} color="inherit" /> : <SearchIcon />
          }
        >
          Search Customers
        </Button>
      </Box>

      {hasSubmitted && (
        <CustomerResultsList
          data={data}
          isLoading={isLoading}
          error={error}
          page={page}
          onPageChange={handlePageChange}
          onSelect={onSelectCustomer}
          selectedCustomerId={selectedCustomerId}
        />
      )}
    </Box>
  );
}

CustomerTab.propTypes = {
  onSelectCustomer: PropTypes.func.isRequired,
  selectedCustomerId: PropTypes.any,
};

export function OrderSearchResultsList({
  data,
  isLoading,
  error,
  page,
  onPageChange,
  onSelect,
  selectedOrderQuery,
}) {
  const classes = useStyles();

  if (isLoading) {
    return <SkeletonRows count={5} />;
  }

  if (error) {
    return (
      <ErrorState
        message="Failed to load orders."
        onRetry={() => onPageChange(page)}
      />
    );
  }

  if (!data) {
    return null;
  }

  const results = data.results || [];

  if (results.length === 0) {
    return (
      <Box className={classes.emptyStateContainer}>
        <ReceiptIcon style={{ fontSize: 40, marginBottom: 8, opacity: 0.3 }} />
        <Typography variant="body2">
          No orders found for that range and restaurant.
        </Typography>
      </Box>
    );
  }

  const totalPages = data.totalPages || null;
  const hasPrev = page > 1;
  const hasMore = totalPages ? page < totalPages : results.length >= 20;

  return (
    <>
      <Box className={classes.resultsList}>
        {results.map((order) => {
          const isSelected = order.displayId === selectedOrderQuery;
           const dateStr = formatOrderEvent(order.createdAtUtc, order.restaurantTimezone);
          const totalStr = order.total != null
            ? `$${Number(order.total).toFixed(2)}`
            : '—';
          return (
            <Box
              key={order.displayId || order.id}
              className={clsx(classes.resultItem, {
                [classes.resultItemSelected]: isSelected,
              })}
              onClick={() => onSelect(order.displayId || String(order.id))}
              role="button"
              tabIndex={0}
              onKeyPress={(e) => {
                if (e.key === 'Enter') onSelect(order.displayId || String(order.id));
              }}
            >
              <Typography className={classes.resultItemPrimary}>
                {order.displayId || order.id} &middot; {order.restaurantName || '—'}
              </Typography>
              <Typography className={classes.resultItemSecondary}>
                {order.status || '—'} &middot; {dateStr}
              </Typography>
              <Typography className={classes.resultItemSecondary}>
                {totalStr}
              </Typography>
              <TipSummary order={order} />
            </Box>
          );
        })}
      </Box>
      {(hasPrev || hasMore) && (
        <Box className={classes.paginationBar}>
          <Button
            size="small"
            disabled={!hasPrev}
            onClick={() => onPageChange(page - 1)}
          >
            Prev
          </Button>
          <Typography variant="caption" color="textSecondary">
            Page {page}
            {totalPages ? ` of ${totalPages}` : ''}
          </Typography>
          <Button
            size="small"
            disabled={!hasMore}
            onClick={() => onPageChange(page + 1)}
          >
            Next
          </Button>
        </Box>
      )}
    </>
  );
}

OrderSearchResultsList.propTypes = {
  data: PropTypes.object,
  isLoading: PropTypes.bool,
  error: PropTypes.any,
  page: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
  selectedOrderQuery: PropTypes.string,
};

export function OrderIdMode({ onSelectOrder, selectedOrderQuery }) {
  const classes = useStyles();

  const [inputValue, setInputValue] = useState('');
  const [submittedQuery, setSubmittedQuery] = useState('');

  const { data, isLoading, error } = useOrderReceipt(submittedQuery);

  useEffect(() => {
    if (data && !isLoading && !error && submittedQuery) {
      onSelectOrder(submittedQuery);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isLoading, error, submittedQuery]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const trimmed = inputValue.trim();
    if (!trimmed) return;
    onSelectOrder(null);
    setSubmittedQuery(trimmed);
  };

  const isNetworkError = !isLoading && error instanceof Error;
  const isNotFound = !isLoading && error && !(error instanceof Error);

  const handleRetry = () => {
    const q = submittedQuery;
    setSubmittedQuery('');
    setTimeout(() => setSubmittedQuery(q), 0);
  };

  return (
    <Box className={classes.leftPanelInner}>
      <Box
        component="form"
        className={classes.formContainer}
        onSubmit={handleSubmit}
        autoComplete="off"
      >
        <TextField
          label="Order ID or display ID"
          variant="outlined"
          size="small"
          fullWidth
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          placeholder="e.g. 12345 or ABC-123"
          style={{ marginBottom: 12 }}
          inputProps={{ 'aria-label': 'order id' }}
        />
        <Button
          type="submit"
          variant="contained"
          color="primary"
          className={classes.submitButton}
          disabled={!inputValue.trim() || isLoading}
          startIcon={
            isLoading ? <CircularProgress size={14} color="inherit" /> : <SearchIcon />
          }
        >
          Look Up Order
        </Button>
      </Box>

      {submittedQuery && (
        <Box className={classes.orderIdResult}>
          {isLoading && <SkeletonRows count={2} />}
          {!isLoading && isNotFound && (
            <Typography variant="body2" color="textSecondary" align="center">
              No order found for &ldquo;{submittedQuery}&rdquo;.
            </Typography>
          )}
          {!isLoading && isNetworkError && (
            <ErrorState
              message="Failed to load order."
              onRetry={handleRetry}
            />
          )}
          {!isLoading && !isNotFound && !isNetworkError && data && (
            <Box
              className={clsx(classes.orderIdResultCard, {
                [classes.orderIdResultCardSelected]:
                  selectedOrderQuery === submittedQuery,
              })}
              onClick={() => onSelectOrder(submittedQuery)}
              role="button"
              tabIndex={0}
              onKeyPress={(e) => {
                if (e.key === 'Enter') onSelectOrder(submittedQuery);
              }}
            >
              <Typography className={classes.resultItemPrimary}>
                {data.displayId || submittedQuery}
              </Typography>
              <Typography className={classes.resultItemSecondary}>
                {data.restaurant?.name || '—'} &middot; {data.status || '—'}
              </Typography>
              <Typography className={classes.resultItemSecondary}>
                {formatOrderEvent(data.timeline?.placedAtUtc, data.restaurant?.timezone)}
                {data.financials?.orderTotal != null &&
                  ` · $${Number(data.financials.orderTotal).toFixed(2)}`}
              </Typography>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}

OrderIdMode.propTypes = {
  onSelectOrder: PropTypes.func.isRequired,
  selectedOrderQuery: PropTypes.string,
};

function OrderDateRangeMode({ onSelectOrder, selectedOrderQuery }) {
  const classes = useStyles();

  const [fromInput, setFromInput] = useState('');
  const [toInput, setToInput] = useState('');
  const [restaurant, setRestaurant] = useState('');
  const [page, setPage] = useState(1);
  const [submittedParams, setSubmittedParams] = useState({});

  const { data, isLoading, error } = useOrderSearch(submittedParams);

  const hasDateInput = fromInput || toInput;

  const handleSubmit = (e) => {
    e.preventDefault();
    const params = {};
    if (fromInput) {
      params.from = Math.floor(new Date(fromInput).getTime() / 1000);
    }
    if (toInput) {
      params.to = Math.floor(new Date(toInput).getTime() / 1000);
    }
    if (restaurant.trim()) params.restaurant = restaurant.trim();
    params.page = 1;
    setPage(1);
    setSubmittedParams(params);
  };

  const handlePageChange = (newPage) => {
    const params = { ...submittedParams, page: newPage };
    setPage(newPage);
    setSubmittedParams(params);
  };

  const hasSubmitted = Object.keys(submittedParams).length > 0;

  return (
    <Box className={classes.leftPanelInner}>
      <Box
        component="form"
        className={classes.formContainer}
        onSubmit={handleSubmit}
        autoComplete="off"
      >
        <TextField
          label="From"
          type="datetime-local"
          variant="outlined"
          size="small"
          fullWidth
          value={fromInput}
          onChange={(e) => setFromInput(e.target.value)}
          InputLabelProps={{ shrink: true }}
          style={{ marginBottom: 12 }}
          inputProps={{ 'aria-label': 'from date time' }}
        />
        <TextField
          label="To"
          type="datetime-local"
          variant="outlined"
          size="small"
          fullWidth
          value={toInput}
          onChange={(e) => setToInput(e.target.value)}
          InputLabelProps={{ shrink: true }}
          style={{ marginBottom: 12 }}
          inputProps={{ 'aria-label': 'to date time' }}
        />
        <TextField
          label="Restaurant name (optional)"
          variant="outlined"
          size="small"
          fullWidth
          value={restaurant}
          onChange={(e) => setRestaurant(e.target.value)}
          placeholder="e.g. Pizza Palace"
          style={{ marginBottom: 12 }}
          inputProps={{ 'aria-label': 'restaurant name' }}
        />
        <Button
          type="submit"
          variant="contained"
          color="primary"
          className={classes.submitButton}
          disabled={!hasDateInput || isLoading}
          startIcon={
            isLoading ? <CircularProgress size={14} color="inherit" /> : <SearchIcon />
          }
        >
          Search Orders
        </Button>
      </Box>

      {hasSubmitted && (
        <OrderSearchResultsList
          data={data}
          isLoading={isLoading}
          error={error}
          page={page}
          onPageChange={handlePageChange}
          onSelect={onSelectOrder}
          selectedOrderQuery={selectedOrderQuery}
        />
      )}
    </Box>
  );
}

OrderDateRangeMode.propTypes = {
  onSelectOrder: PropTypes.func.isRequired,
  selectedOrderQuery: PropTypes.string,
};

function OrderTab({ onSelectOrder, selectedOrderQuery }) {
  const classes = useStyles();
  const [orderMode, setOrderMode] = useState('id');

  return (
    <Box className={classes.leftPanelInner}>
      <Box className={classes.formContainer} style={{ paddingBottom: 8 }}>
        <RadioGroup
          row
          value={orderMode}
          onChange={(e) => setOrderMode(e.target.value)}
          className={classes.modeToggleGroup}
          aria-label="order lookup mode"
        >
          <FormControlLabel
            value="id"
            control={<Radio size="small" color="primary" />}
            label={<Typography variant="body2">ID Lookup</Typography>}
          />
          <FormControlLabel
            value="search"
            control={<Radio size="small" color="primary" />}
            label={<Typography variant="body2">Date Range Search</Typography>}
          />
        </RadioGroup>
      </Box>

      {orderMode === 'id' ? (
        <OrderIdMode
          onSelectOrder={onSelectOrder}
          selectedOrderQuery={selectedOrderQuery}
        />
      ) : (
        <OrderDateRangeMode
          onSelectOrder={onSelectOrder}
          selectedOrderQuery={selectedOrderQuery}
        />
      )}
    </Box>
  );
}

OrderTab.propTypes = {
  onSelectOrder: PropTypes.func.isRequired,
  selectedOrderQuery: PropTypes.string,
};

export function LookupPage() {
  const classes = useStyles();
  const isDesktop = useIsDesktop();

  const [activeTab, setActiveTab] = useState(0);
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [selectedOrderQuery, setSelectedOrderQuery] = useState(null);

  const handleTabChange = (_, newValue) => {
    setActiveTab(newValue);
    setSelectedCustomerId(null);
    setSelectedOrderQuery(null);
  };

  const handleSelectCustomer = (customerId) => {
    setSelectedCustomerId(customerId);
    setSelectedOrderQuery(null);
  };

  const handleSelectOrder = (query) => {
    setSelectedOrderQuery(query);
    setSelectedCustomerId(null);
  };

  const handleSelectOrderFromCustomer = (query) => {
    setSelectedOrderQuery(query);
  };

  const hasSelection = selectedCustomerId != null || selectedOrderQuery != null;

  const leftPanel = (
    <Box className={classes.leftPanelInner}>
      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        indicatorColor="primary"
        textColor="primary"
        variant="fullWidth"
        className={classes.tabsRoot}
        aria-label="lookup tabs"
      >
        <Tab label="Customer" aria-label="customer search tab" />
        <Tab label="Order" aria-label="order search tab" />
      </Tabs>

      {activeTab === 0 && (
        <CustomerTab
          onSelectCustomer={handleSelectCustomer}
          selectedCustomerId={selectedCustomerId}
        />
      )}
      {activeTab === 1 && (
        <OrderTab
          onSelectOrder={handleSelectOrder}
          selectedOrderQuery={selectedOrderQuery}
        />
      )}
    </Box>
  );

  const clearRightPanel = () => {
    setSelectedOrderQuery(null);
    setSelectedCustomerId(null);
  };

  const rightPanelContent = (() => {
    if (selectedOrderQuery != null) {
      return (
        <OrderReceiptPanel
          query={selectedOrderQuery}
          onClose={clearRightPanel}
        />
      );
    }
    if (selectedCustomerId != null) {
      return (
        <CustomerDetailPanel
          customerId={selectedCustomerId}
          onClose={clearRightPanel}
          onSelectOrder={handleSelectOrderFromCustomer}
        />
      );
    }
    return (
      <Box className={classes.emptyStateContainer}>
        <SearchIcon style={{ fontSize: 64, marginBottom: 12, opacity: 0.25 }} />
        <Typography variant="body1" color="textSecondary">
          Select a result to view details
        </Typography>
      </Box>
    );
  })();

  const handleMobileBack = clearRightPanel;

  const mobileHeaderTitle = (() => {
    if (selectedOrderQuery != null) return `Order: ${selectedOrderQuery}`;
    if (selectedCustomerId != null) return `Customer #${selectedCustomerId}`;
    return '';
  })();

  if (isDesktop) {
    return (
      <main className={classes.content} aria-label="lookup page">
        <LeftMenuDrawer />
        <Box className={classes.header}>
          <SearchMenuBar title="Lookup" />
        </Box>
        <Box className={classes.body}>
          <Box className={classes.desktopPanelLeft}>
            <Typography className={classes.panelTitle}>Search</Typography>
            {leftPanel}
          </Box>
          <Box className={classes.desktopPanelRight}>
            {rightPanelContent}
          </Box>
        </Box>
      </main>
    );
  }

  return (
    <main className={classes.content} aria-label="lookup page">
      <LeftMenuDrawer />
      <Box className={classes.header}>
        {hasSelection ? (
          <Box display="flex" alignItems="center">
            <IconButton
              size="small"
              onClick={handleMobileBack}
              aria-label="back"
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h6" style={{ marginLeft: 8 }}>
              {mobileHeaderTitle}
            </Typography>
          </Box>
        ) : (
          <SearchMenuBar title="Lookup" />
        )}
      </Box>

      <Box className={classes.body}>
        {hasSelection ? (
          <Box className={classes.mobilePanelFull}>
            {rightPanelContent}
          </Box>
        ) : (
          <Box className={classes.mobilePanelFull}>
            {leftPanel}
          </Box>
        )}
      </Box>
    </main>
  );
}
